import { AccordionDetails, Stack, Typography } from "@mui/material";
import { type MerchantDetailDto, ProductCode } from "Infrastructure/Api/Api";
import { useGetOrbitLStatusQuery } from "Api/Queries/Merchants/useGetOrbitLStatusQuery";
import { useCurrentUserAccessRole } from "Hooks/useCurrentUserAccessRole";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { canManageOrbitLTerminals } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/orbitLTerminalManagement";
import { ProductActivationForm } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/ProductActivationForm/ProductActivationForm";
import { getA11yIDs } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/utils";
import { Colors } from "Components/Layout/Themes/Colors";
import type { ProductPricingsType } from "Components/Hunter/Merchants/types";
import { ProductDetail } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/ProductDetail";
import type { ProductPricingFormData } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/ProductActivationForm/schema";
import type { AppProductsVariant } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";
import type { PrerequisiteItem } from "Components/Shared/Merchant/Products/AppProducts/types";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

const ACCORDION_RESOURCES =
  Resources.Components.Merchant.AppProducts.ProductAccordion;

type Props = {
  code: ProductCode;
  isActive: boolean;
  isDefault: boolean;
  variant: AppProductsVariant;
  merchantPublicID: string;
  merchantData: MerchantDetailDto;
  allGroupedPricings: ProductPricingsType;
  isOnboarding?: boolean;
  prerequisites?: Array<PrerequisiteItem>;
};

export const AccordionContent = ({
  code,
  isActive,
  isDefault,
  variant,
  merchantData,
  merchantPublicID,
  allGroupedPricings,
  isOnboarding,
  prerequisites,
}: Props) => {
  const { t } = useResource();
  const userRole = useCurrentUserAccessRole();
  const isSoftPosToActivate =
    code === ProductCode.SoftPos && !isActive && variant !== "readonly";
  const { data: orbitLStatus, isLoading: isOrbitLStatusLoading } =
    useGetOrbitLStatusQuery(merchantPublicID, isSoftPosToActivate);
  const isSoftPosStatusLoading = isSoftPosToActivate && isOrbitLStatusLoading;
  const isSoftPosLockedBehindContract =
    isSoftPosToActivate && !!orbitLStatus?.isTerminalCountLocked;

  const sortedPricings = (
    allGroupedPricings.find((p) => p.code === code)?.variants || []
  ).sort((a, b) => a.totalFee - b.totalFee);

  const mostExpensivePricingID = sortedPricings.at(-1)?.id;
  const defaultValues: Partial<ProductPricingFormData> = {
    productPricingID: mostExpensivePricingID,
  };

  if (isActive) {
    return (
      <Layout code={code}>
        <ProductDetail
          code={code}
          isDefault={isDefault}
          variant={variant}
          merchantData={merchantData}
          productPricings={sortedPricings}
          merchantPublicID={merchantPublicID}
          isOnboarding={isOnboarding}
        />
      </Layout>
    );
  }

  if (variant === "readonly") {
    return (
      <Layout code={code}>
        <Typography>{t(ACCORDION_RESOURCES.ProductNotActivated)}</Typography>
      </Layout>
    );
  }

  // Until the status is in, the card can't tell the form from the lock notice, so it shows neither.
  if (isSoftPosStatusLoading) {
    return (
      <Layout code={code}>
        <CenteredCircularProgress color="grey" size={35} />
      </Layout>
    );
  }

  // Once the Orbit L order is with Comgate the activation endpoint refuses SoftPOS for everyone but
  // staff (staff never sees the lock), so the card points to the change request instead of a
  // button that can only fail.
  if (isSoftPosLockedBehindContract) {
    const canRequest = canManageOrbitLTerminals({
      userRole,
      isOnboarding,
      orbitLStatus,
    });

    return (
      <Layout code={code}>
        <Typography>
          {t(
            canRequest
              ? ACCORDION_RESOURCES.SoftPosViaChangeRequest
              : ACCORDION_RESOURCES.SoftPosContactSupport,
          )}
        </Typography>
      </Layout>
    );
  }

  return (
    <Layout code={code}>
      <ProductActivationForm
        code={code}
        defaultValues={defaultValues}
        productPricings={sortedPricings}
        merchantPublicID={merchantPublicID}
        variant={variant}
        prerequisites={prerequisites}
      />
    </Layout>
  );
};

type LayoutProps = {
  code: ProductCode;
  children?: ReactNode;
};

const Layout = ({ children, code }: LayoutProps) => {
  const { headerID, contentID } = getA11yIDs(code);

  return (
    <AccordionDetails
      id={contentID}
      aria-labelledby={headerID}
      sx={{ p: 5, borderTop: `1px solid ${Colors.Black}10` }}
    >
      <Stack>{children}</Stack>
    </AccordionDetails>
  );
};
